// "An update is available" when a newer bundle is live, "An update is required" when it
// speaks a newer API epoch or the API has refused this one
// (docs/Y2026-Q4/design-api-epoch.md). Never reloads by itself: a tab may hold unsaved
// work, and an outdated one only holds what it loaded and refuses to write.
import { useEffect, useState } from "react";

import { API_EPOCH, epochOf, useClientOutdated } from "../api/apiEpoch";
import { NoticeBanner } from "./NoticeBanner";

const CHECK_EVERY_MS = 5 * 60_000;
// Required but not yet reloadable: the API goes live minutes before the bundle.
const WAITING_CHECK_EVERY_MS = 5_000;

// A deploy gives the app a new hashed entry script, so a tab whose loaded
// script differs from the one the live index.html names is running old code.
const entryScript = (doc: Document): string | null =>
  doc
    .querySelector<HTMLScriptElement>('script[type="module"][src]')
    ?.getAttribute("src") ?? null;

interface LiveVersion {
  /** The live bundle is not this tab's. */
  newer: boolean;
  /** The live bundle speaks a newer API epoch than this tab's. */
  newerEpoch: boolean;
  /** The live page states an epoch at all. */
  epochKnown: boolean;
}

const useLiveVersion = (everyMs: number): LiveVersion => {
  const [live, setLive] = useState<LiveVersion>({
    newer: false,
    newerEpoch: false,
    epochKnown: false,
  });
  useEffect(() => {
    // The dev server has no hashed build to compare.
    if (import.meta.env.DEV) return;
    const loaded = entryScript(document);
    if (!loaded) return;
    let stopped = false;
    const check = async () => {
      if (stopped || document.visibilityState !== "visible") return;
      try {
        const res = await fetch("/index.html", { cache: "no-store" });
        if (!res.ok) return;
        const page = new DOMParser().parseFromString(await res.text(), "text/html");
        const script = entryScript(page);
        const epoch = epochOf(page);
        if (stopped || !script) return;
        setLive({
          newer: script !== loaded,
          newerEpoch: epoch !== null && API_EPOCH !== null && epoch > API_EPOCH,
          epochKnown: epoch !== null,
        });
      } catch {
        // Offline or mid-deploy: the next check tries again.
      }
    };
    const timer = window.setInterval(check, everyMs);
    document.addEventListener("visibilitychange", check);
    void check();
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
    };
  }, [everyMs]);
  return live;
};

/** ``offerAvailable``: also say when an update is merely available. Someone outside InRange
 * is told only when one is required, since their tab then refuses to write. */
export const NewVersionBanner = ({ offerAvailable }: { offerAvailable: boolean }) => {
  const refused = useClientOutdated();
  const [waiting, setWaiting] = useState(false);
  const live = useLiveVersion(waiting ? WAITING_CHECK_EVERY_MS : CHECK_EVERY_MS);
  const required = refused || live.newerEpoch;
  // Reloading must land on a bundle the API serves: when an update is required, only a
  // live bundle speaking a newer epoch will do, not merely one newer than this tab's.
  const reloadable =
    import.meta.env.DEV ||
    (required && live.epochKnown ? live.newerEpoch : live.newer);
  useEffect(() => setWaiting(required && !reloadable), [required, reloadable]);
  const [reloading, setReloading] = useState(false);
  if (!required && !(offerAvailable && live.newer)) return null;
  const reload = () => {
    setReloading(true);
    window.location.reload();
  };
  if (required) {
    return (
      <NoticeBanner
        tone="required"
        action={reloading ? "Reloading…" : reloadable ? "Reload" : "Updating…"}
        onAction={reload}
        busy={reloading || !reloadable}
      >
        An update is required. Reload to continue.
      </NoticeBanner>
    );
  }
  return (
    <NoticeBanner
      action={reloading ? "Reloading…" : "Reload"}
      onAction={reload}
      busy={reloading}
    >
      An update is available. Reload to get the latest.
    </NoticeBanner>
  );
};
